<template>
  <div
    class="absolute top-0 left-0 bottom-0 z-10 flex transition-transform duration-300 ease-in-out"
    :class="collapsed ? '-translate-x-full' : 'translate-x-0'"
  >
    <!-- Panel body -->
    <div class="w-screen sm:w-[500px] lg:w-[500px] h-full bg-white shadow-xl overflow-y-auto">
      <div class="flex flex-col gap-4 sm:gap-5 p-4 sm:p-5 pb-20 sm:pb-5">
        <!-- Header -->
        <div class="flex items-center gap-3 w-full">
          <h2 class="font-title font-bold text-lg text-primary-dark flex-1">
            {{ labels.title }}
          </h2>
          <div class="hidden sm:flex">
            <button
              type="button"
              :class="viewType === 'list' ? 'bg-primary-dark text-white' : 'bg-white text-primary-dark border border-primary-dark'"
              class="rounded-l-xl p-1.5 w-9 transition"
              @click="$emit('view-change', 'list')"
            >
              <i class="ph ph-list-dashes text-base"></i>
            </button>
            <button
              type="button"
              :class="viewType === 'grid' ? 'bg-primary-dark text-white' : 'bg-white text-primary-dark border border-primary-dark'"
              class="rounded-r-xl p-1.5 w-9 transition"
              @click="$emit('view-change', 'grid')"
            >
              <i class="ph ph-grid-four text-base"></i>
            </button>
          </div>
        </div>

        <!-- Results count -->
        <div v-if="!loading && total > 0" class="text-sm text-neutral">
          {{ labels.results_count.replace(':total', total) }}
        </div>

        <!-- Truncation warning -->
        <div v-if="mapPointsTruncated" class="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-xs text-yellow-800">
          <i class="ph ph-warning mr-1"></i>
          {{ labels.zoom_in_hint }}
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="flex flex-col gap-3">
          <div v-for="i in 3" :key="i" class="animate-pulse rounded-2xl bg-neutral-light h-36"></div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="text-center py-8 text-neutral">
          <i class="ph ph-warning-circle text-3xl mb-2 block"></i>
          <p class="text-sm">{{ labels.error_loading }}</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="properties.length === 0" class="text-center py-8 text-neutral">
          <i class="ph ph-map-pin text-3xl mb-2 block"></i>
          <p class="font-bold mb-1 text-sm">{{ labels.no_results }}</p>
          <p class="text-xs">{{ labels.no_results_hint }}</p>
        </div>

        <!-- List view -->
        <div v-else-if="viewType === 'list'" class="flex flex-col gap-4 sm:gap-5">
          <PropertyCard
            v-for="property in properties"
            :key="property.id"
            :property="property"
            variant="list"
            :labels="labels"
            @highlight="$emit('highlight', $event)"
            @select="$emit('select', $event)"
          />
        </div>

        <!-- Grid view -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <PropertyCard
            v-for="property in properties"
            :key="property.id"
            :property="property"
            variant="grid"
            :labels="labels"
            @highlight="$emit('highlight', $event)"
            @select="$emit('select', $event)"
          />
        </div>

        <!-- Pagination -->
        <Pagination
          v-if="!loading && properties.length > 0"
          :current-page="currentPage"
          :last-page="lastPage"
          :labels="labels"
          @page-change="$emit('page-change', $event)"
        />
      </div>
    </div>

    <!-- Collapse toggle tab (desktop only) -->
    <button
      class="hidden sm:flex items-center justify-center w-6 h-12 bg-white shadow-md rounded-r-lg self-center flex-none"
      @click="$emit('toggle-collapse')"
      :title="collapsed ? labels.expand_panel : labels.collapse_panel"
    >
      <i
        class="ph text-neutral-dark text-sm"
        :class="collapsed ? 'ph-caret-right' : 'ph-caret-left'"
      ></i>
    </button>
  </div>
</template>

<script setup>
import PropertyCard from './PropertyCard.vue';
import Pagination from './Pagination.vue';

defineProps({
  properties: { type: Array, default: () => [] },
  viewType: { type: String, default: 'grid' },
  loading: { type: Boolean, default: false },
  error: { type: [String, null], default: null },
  total: { type: Number, default: 0 },
  currentPage: { type: Number, default: 1 },
  lastPage: { type: Number, default: 1 },
  mapPointsTruncated: { type: Boolean, default: false },
  labels: { type: Object, required: true },
  collapsed: { type: Boolean, default: false },
});

defineEmits(['view-change', 'page-change', 'highlight', 'select', 'toggle-collapse']);
</script>
